import React, { useState, useMemo, useEffect } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import "../../css/barcodeComponent.css";
import "primeflex/primeflex.css";
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primeicons/primeicons.css";
import { useSetRecoilState, useRecoilValue, useRecoilState } from "recoil";
import { orderInfoIsViewState } from "../../stores/barcode/orderInfoIsViewState";
import { barcodeDataState } from "../../stores/barcode/barcodeDataState";
import { trackingValueState } from "../../stores/barcode/trackingValueState";
import { createTitle } from "../../utils/createTitle";
import { loginState } from "../../stores/loginState";
import useGetBarcodeData from "../../api/barcode/useGetBarcodeData";
import { useGetAutoCheck } from "../../api/barcode/useGetAutoCheck";
import { useGetAutoChange } from "../../api/barcode/useGetAutoChange";
import { useSendInvoice } from "../../api/barcode/useSendInvoice";
import { useGetTracking } from "../../api/barcode/useGetTracking";
import Loading from "../Loading";
import { formatNumberCommas } from "../../utils/formatNumberCommas";
import { downloadExcel } from "./DownloadExcel"; // 엑셀 다운로드 함수 가져오기
import Swal from "sweetalert2";
import { isBarcodeLoading } from "../../stores/barcode/isBarcodeLoading";

export default function BarcodeListsComponent(props) {
  // NOTE 상태 변수 선언
  const login = useRecoilValue(loginState); //로그인 전역변수
  const barcodeData = useRecoilValue(barcodeDataState); // 운송장 데이터
  const setOrderInfoIsView = useSetRecoilState(orderInfoIsViewState); // 화물추적 데이터 표시여부
  const [trackingValue, setTrackingValue] = useRecoilState(trackingValueState); // 화물추적 데이터

  const [selectedProduct, setSelectedProduct] = useState(null); // 특정 운송장 선택여부
  const today = new Date().toISOString().split("T")[0];
  const [isLoading, setIsLoading] = useRecoilState(isBarcodeLoading); // 로딩 상태 추가
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [workType, setWorkType] = useState("전체");
  const [barcode, setBarcode] = useState("");
  const [isOn, setIsOn] = useState(true);

  // TODO 운송장 데이터 호출 API
  const { mutateAsync: getBarcodeData } = useGetBarcodeData({
    startDate,
    endDate,
    workType,
    barcode,
  });

  // TODO 자동여부 확인 API
  const { mutate: autoCheckMutate } = useGetAutoCheck(setIsOn);

  // TODO 자동여부 on/off API
  const { mutate: autoChangeMutate } = useGetAutoChange(isOn, setIsOn);

  // TODO 해당 고객사에 즉시(수동)전송 API
  const { mutate: sendInvoiceMutate } = useSendInvoice({startDate, endDate});

  // TODO 화물추적 API
  const { mutate: getTracking } = useGetTracking(trackingValue.barcode);

  // NOTE 운송장 데이터 업데이트 시
  useEffect(() => {
    // 화물추적 표시여부 기본적으로 비활성화
    setOrderInfoIsView(false);

    // 운송장 데이터가 존재한다면 조회일자 설정
    if (barcodeData && barcodeData.length > 0) {
      const dates = barcodeData.map((data) => new Date(data.scandate));
      const minDate = new Date(Math.min(...dates));
      const maxDate = new Date(Math.max(...dates));
      setStartDate(minDate.toISOString().split("T")[0]);
      setEndDate(maxDate.toISOString().split("T")[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [barcodeData]);

  // NOTE 특정 운송장 선택 함수
  const onSelectionChange = (e) => {
    setSelectedProduct(e.value); // 특정 운송장 열 색상 변경
    setOrderInfoIsView(!!e.value); // 화물추적 표시여부 설정
  };

  // NOTE 운송장 정보 표기 양식
  const title = useMemo(
    () =>
      createTitle([
        { label: "운송장번호", key: "barcode" },
        { label: "스캔일자", key: "scandate" },
        { label: "스캔시간", key: "scantime" },
        { label: "차량번호", key: "car_num" },
        { label: "업무구분", key: "tm_dv" },
        { label: "작업영업소", key: "bran_cd" },
        { label: "작업영업소사원", key: "emp_cd" },
        { label: "상대영업소", key: "tg_bran_cd" },
        { label: "상대영업소사원", key: "pob" },
        { label: "이미지 존재여부", key: "img_exists"},
        { label: "스캔타입", key: "dev_serial"}
      ]),
    []
  );

  // 한진 운송장 정보 표기
  const hanjinTitle = useMemo(
    () =>
      createTitle([
        { label: "운송장번호", key: "barcode" },
        { label: "스캔일자", key: "scandate" },
        { label: "스캔시간", key: "scantime" },
        { label: "간선편명", key: "trk" },
        { label: "업무구분", key: "tm_dv" },
        { label: "작업영업소", key: "bran_cd" },
        { label: "작업영업소사원", key: "emp_cd" },
        { label: "상대영업소사원", key: "pob" },
      ]),
    []
  );

  // 한덱스 운송장 정보 표기
  const handexTitle = useMemo(
    () =>
      createTitle([
        { label: "운송장번호", key: "barcode" },
        { label: "스캔일자", key: "scandate" },
        { label: "스캔시간", key: "scantime" },
        { label: "업무구분", key: "tm_dv" },
        { label: "작업영업소", key: "bran_cd" },
        { label: "작업영업소사원", key: "emp_cd" },
        { label: "상대영업소사원", key: "pob" },
      ]),
    []
  );

  // NOTE 운송장 정보 표기
  const selectedTitle = useMemo(() => {
    if (login.company === "HANJIN") {
      return hanjinTitle;
    } else if (login.company === "HANDEX") {
      return handexTitle;
    } else {
      return title;
    }
  }, [login.company, title, hanjinTitle, handexTitle]);

  // NOTE 고객사 전송관련 호칭
  const companyAutoComment =
    login.company === "LOGEN"
      ? "iLogen" // 로젠
      : login.company === "LOTTE"
      ? "알프스" // 롯데
      : "";

  // NOTE 자동여부 확인 함수
  useEffect(() => {
    if (login.company === "LOGEN" || login.company === "LOTTE") { // 로젠, 롯데만
      autoCheckMutate();
    }
  }, [autoCheckMutate, login.company]);

  // NOTE 자동전송여부 토글 조정
  const handleToggle = async () => {
    const newAutoValue = isOn ? "0" : "1";
    try {
      await autoChangeMutate(newAutoValue); // Ensure mutation completes before updating UI
    } catch (error) {
      console.error("Error updating auto change:", error);
    }
  };

  // NOTE 즉시전송 클릭 시
  const onClickSendInvoice = () => {
    sendInvoiceMutate();
  };

  // NOTE 운송장 데이터 조회 시
  const onClickSearch = async () => {
    if (startDate <= endDate) {
      setIsLoading(true); // 로딩 시작
      try {
        await getBarcodeData(); // 비동기 호출
      } catch (error) {
        console.error("운송장 데이터 호출 중 문제가 발생하였습니다! ", error);
      } finally {
        setIsLoading(false); // 로딩 끝
      }
    } else {
      Swal.fire({ // 이전일자가 이후일자보다 날짜가 늦을 시
        icon: "warning",
        title: "조회일자가 올바르지 않습니다!",
        confirmButtonText: "확인",
      });
    }
    setOrderInfoIsView(false);
  };

  // NOTE 데이터 값을 변환하는 함수
  const workTypeTmdv = (rowData) => {
    if (rowData.tm_dv === "60") {
      return "배송입고(60)";
    } else if (rowData.tm_dv === "30") {
      return "집하출고(30)";
    } else if (rowData.tm_dv === "21") {
      return "도착(21)";
    } else if (rowData.tm_dv === "20") {
      return "발송(20)";
    }else if (rowData.tm_dv === "31") {
      return "간선상차(31)";
    }else if (rowData.tm_dv === "32") {
      return "간선하차(32)";
    }else if (rowData.tm_dv === "14") {
      return "입고(14)";
    }else if (rowData.tm_dv === "DD") {
      return "배송인수인계(DD)";
    }
    return rowData.tm_dv;
  };

  // NOTE 데이터 값을 변환하는 함수
  const workTypeRownum = (rowData) => {
    if (rowData.row_num) {
      return formatNumberCommas(rowData.row_num);
    }
    return rowData.row_num;
  };

  // NOTE 데이터 값을 변환하는 함수
  const workTypeImg = (rowData) => {
    if (rowData.img_exists) {
      return "O";
    }
    else return "X";
  };

  // NOTE 데이터 값을 변환하는 함수
  const workTypeSerial = (rowData) => {
    if (rowData.dev_serial === "1541084") {
      return "오토스캐너";
    }
    else if (rowData.dev_serial === "1793913") {
      return "PDA";
    }
    else return "기타";
  };

  // NOTE 특정 운송장 클릭 시
  const handleRowClick = async (rowData) => {
    setTrackingValue({
      barcode: rowData.barcode,
      scanDate: rowData.scandate,
    });
    setTimeout(() => {
      getTracking(); // 회물추적 데이터 호출
    }, 0);
  };
  
  // NOTE Excel 저장 버튼 클릭 시
  const onClickExcelDownload = () => {
    downloadExcel(
      barcodeData,
      `${
        startDate === endDate ? startDate : startDate + "~" + endDate
      }운송장 데이터 조회.xlsx`
    );
  };

  // NOTE 운송장 번호에서 Enter 키 클릭 시
  const handleKeyDownEnter = (e) => {
    if (e.key === "Enter") {
      onClickSearch();
    }
  };

  return (
    <div
      onKeyDown={handleKeyDownEnter}
      className="card"
      style={{
        width: "1100px",
        minHeight: "800px",
        border: `1px solid ${props.theme.white4}`,
        background: props.theme.white1,
        color: props.theme.black1,
      }}
    >
      {isLoading && <Loading />}
      <span
        style={{
          display: "flex",
          width: "100%",
          justifyContent: "space-between",
          padding: 0,
        }}
      >
        <h3 style={{ position: "relative", left: "0", margin: "0" }}>
          운송장관리
        </h3>
        <span style={{ display: "flex", padding: 0 }}>
          
        <div
          style={{
            textAlign: "right",
            marginLeft: "30px",
            color: props.theme.black1,
            fontSize: "16px",
          }}>
            <div style={{ display:"flex", justifyContent: "flex-end"}}>
            {login.company === "LOGEN" ? "배송입고 (60) " :
              login.company === "LOTTE" ? "도 착 (21) " : "간선하차 (32) "}:{" "}
              <p style={{width:"100px"}}>
                {barcodeData
                  ? formatNumberCommas(
                      barcodeData.filter((item) => item.tm_dv === "60" || item.tm_dv === "21" || item.tm_dv === "32" || item.tm_dv === "15").length
                    )
                  : 0}{" "}
                건 
              </p>
            </div>
            <div style={{ display:"flex", justifyContent: "flex-end"}}>
            {login.company === "LOGEN" ? "집하출고 (30) " :
              login.company === "LOTTE" ? "발 송 (20) " : "간선상차 (31) "}:{" "}
              <p style={{width:"100px"}}>
                {barcodeData
                  ? formatNumberCommas(
                      barcodeData.filter((item) => item.tm_dv === "30" || item.tm_dv === "20" || item.tm_dv === "31" || item.tm_dv === "50").length
                    )
                  : 0}{" "}
                건
              </p>
            </div>
          </div>
          
          {(login.company === "HANJIN" || login.company === "HANDEX") && (
            <div
              style={{
                textAlign: "right",
                marginLeft: "30px",
                color: props.theme.black1,
                fontSize: "16px",
              }}>
              <div style={{ display:"flex", justifyContent: "flex-end"}}>
              {"입고 (14) "}:{" "}
              <p style={{width:"100px"}}>
                {barcodeData
                  ? formatNumberCommas(
                      barcodeData.filter((item) => item.tm_dv === "14").length
                    )
                  : 0}{" "}
                건 
              </p>
              </div>
              <div style={{ display:"flex", justifyContent: "flex-end"}}>
                {"배송인수인계 (DD) "}:{" "}
                <p style={{width:"100px"}}>
                  {barcodeData
                    ? formatNumberCommas(
                        barcodeData.filter((item) => item.tm_dv === "DD" ).length
                      )
                    : 0}{" "}
                  건
                </p>
              </div>
            </div>
          )}
          {(login.company === "LOGEN" || login.company === "LOTTE") && (
            <>
              <label className="toggle" style={{marginLeft: 50}}>
                <span>{companyAutoComment} 자동전송</span>
                <input
                  role="switch"
                  type="checkbox"
                  onChange={handleToggle}
                  checked={isOn}
                />
              </label>
              <button
                style={{
                  padding: "5px 15px",
                  border: `1px solid ${props.theme.white1}`,
                  background: props.theme.colorwhite1,
                  color: props.theme.black1,
                  borderRadius: "10px",
                  marginLeft: "40px",
                }}
                onClick={onClickSendInvoice}
              >
                {companyAutoComment} 수동전송
              </button>
            </>
          )}
          <button
            style={{
              padding: "5px 15px",
              border: `1px solid ${props.theme.white1}`,
              background: props.theme.colorwhite1,
              color: props.theme.black1,
              borderRadius: "10px",
              marginLeft: "20px",
            }}
            onClick={onClickExcelDownload}
          >
            Excel 저장
          </button>
        </span>
      </span>
      <span
        className="filter"
        style={{
          border: `1px solid ${props.theme.white1}`,
          background: props.theme.white2,
          color: props.theme.black1,
        }}
      >
        <p>조회일자</p>
        <input
          type="date"
          className="date"
          style={{ background: props.theme.white0 }}
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
        />
        -
        <input
          type="date"
          className="date"
          style={{ background: props.theme.white0 }}
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
        />
        <div style={{ margin: "0 10px" }}>|</div>
        <p>업무구분</p>
        <select
          className="date"
          value={workType}
          onChange={(e) => setWorkType(e.target.value)}
          style={{ background: props.theme.white0, width: "120px" }}
        >
          <option>전체</option>
          <option value={login.company === "LOGEN" ? "60" :
              login.company === "LOTTE" ? "21" : 
              login.company === "HANJIN" ? "31" : "15"}>
            {login.company === "LOGEN" ? "배송입고 (60)" :
              login.company === "LOTTE" ? "도 착 (21)" :  "간선하차 (32)"}
          </option>
          <option value={login.company === "LOGEN" ? "30" :
              login.company === "LOTTE" ? "20" : 
              login.company === "HANJIN" ? "32" : "50"}>
            {login.company === "LOGEN" ? "집하출고 (30)" :
              login.company === "LOTTE" ? "발 송 (20)" :  "간선상차 (31)"}
          </option>
        </select>
        <div style={{ margin: "0 10px" }}>|</div>
        <p>운송장 번호</p>
        <input
          placeholder="번호를 입력해주세요."
          className="date"
          value={barcode}
          onChange={(e) => setBarcode(e.target.value)}
          style={{ background: props.theme.white0 }}
        />
        <button
          className="date"
          id="button"
          style={{ background: props.theme.white3, width: "70px" }}
          onClick={onClickSearch}
        >
          검색
        </button>
      </span>
      <DataTable
        value={barcodeData}
        rows={25}
        rowsPerPageOptions={[5, 10, 25, 50]}
        tableStyle={{ minWidth: "50rem" }}
        currentPageReportTemplate="{first} to {last} of {totalRecords}"
        paginatorLeft={
          <div
            style={{
              position: "absolute",
              left: 0,
              color: props.theme.black1,
              fontSize: "18px",
              bottom: "15px",
            }}
          >
            총 검색 결과:{" "}
            {barcodeData ? formatNumberCommas(barcodeData.length) : 0} 건
          </div>
        }
        paginatorRight={<></>}
        selectionMode="single"
        selection={selectedProduct}
        onSelectionChange={onSelectionChange}
        dataKey="id"
        emptyMessage=" "
        paginator
        paginatorClassName="Indigo 200"
      >
        {selectedTitle.map((col, index) => (
          <Column
            key={col.key || index}
            field={col.key}
            header={col.label}
            sortable={false}
            body={(rowData) => (
              <div
                onClick={() => handleRowClick(rowData)} // 클릭 시 핸들러 호출
              >
                {col.key === "row_num"
                  ? workTypeRownum(rowData)
                  : col.key === "tm_dv"
                  ? workTypeTmdv(rowData)
                  : col.key === "img_exists"
                  ? workTypeImg(rowData)
                  : col.key === "dev_serial"
                  ? workTypeSerial(rowData)
                  : rowData[col.key]}
              </div>
            )}
            style={{
              fontSize: "13px",
              width: "max-content",
              height: "10px",
              padding: "0px 5px",
              border: `1px solid ${props.theme.white4}`,
              backgroundColor: "unset",
              color: props.theme.black1,
              textAlign: "center",
              cursor: "pointer", // 클릭 가능한 커서 스타일
            }}
          />
        ))}
      </DataTable>
    </div>
  );
}
